import { useState, useEffect } from 'react';
import { ptAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Loader2, Plus, Trash2, Edit, Save, Globe, Calendar } from 'lucide-react';


const ProfessionalTaxSettings = () => {
    const { dialogProps, confirm } = useConfirmation();
    const [configs, setConfigs] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState(null);
    const [formData, setFormData] = useState({
        state_name: '',
        is_enabled: true,
        deduction_frequency: 'monthly',
        deduction_months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        slabs: []
    });

    useEffect(() => {
        fetchConfigs(true);
    }, []);

    const fetchConfigs = async (selectFirst = false) => {
        try {
            const response = await ptAPI.getConfigs();
            setConfigs(response.data);
            if (response.data.length > 0) {
                // If requested, or if current selectedConfigId is invalid, select the first config
                if (selectFirst || !selectedConfigId || !response.data.some(c => c.id === selectedConfigId)) {
                    setSelectedConfigId(response.data[0].id);
                }
            } else {
                setSelectedConfigId(null);
            }
        } catch (error) {
            toast.error("Failed to fetch PT configurations");
        } finally {
            setLoading(false);
        }
    };


    const handleOpenCreate = () => {
        setEditingConfig(null);
        setFormData({
            state_name: '',
            is_enabled: true,
            deduction_frequency: 'monthly',
            deduction_months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
            slabs: [{ min_salary: 0, max_salary: '', amount: 0, special_amount: '', special_month: '' }]
        });
        setIsOpen(true);
    };

    const handleOpenEdit = (cfg) => {
        setEditingConfig(cfg);
        setFormData({
            state_name: cfg.state_name,
            is_enabled: cfg.is_enabled,
            deduction_frequency: cfg.deduction_frequency || 'monthly',
            deduction_months: cfg.deduction_months || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
            slabs: cfg.slabs.map(slab => ({
                min_salary: slab.min_salary,
                max_salary: slab.max_salary === 9999999 ? '' : slab.max_salary,
                amount: slab.amount,
                special_amount: slab.special_amount || '',
                special_month: slab.special_month || ''
            }))
        });
        setIsOpen(true);
    };

    const handleFrequencyChange = (freq) => {
        let months = [];
        if (freq === 'monthly') {
            months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        } else if (freq === 'half_yearly') {
            months = [9, 3];
        } else if (freq === 'yearly') {
            months = [3];
        }
        setFormData(prev => ({
            ...prev,
            deduction_frequency: freq,
            deduction_months: months
        }));
    };

    const handleMonthToggle = (m) => {
        setFormData(prev => {
            const current = prev.deduction_months || [];
            const updated = current.includes(m)
                ? current.filter(x => x !== m)
                : [...current, m].sort((a, b) => a - b);
            return { ...prev, deduction_months: updated };
        });
    };

    const handleAddSlab = () => {
        setFormData(prev => ({
            ...prev,
            slabs: [...prev.slabs, { min_salary: '', max_salary: '', amount: '', special_amount: '', special_month: '' }]
        }));
    };

    const handleRemoveSlab = (index) => {
        setFormData(prev => ({
            ...prev,
            slabs: prev.slabs.filter((_, i) => i !== index)
        }));
    };

    const handleSlabChange = (index, field, value) => {
        const updated = [...formData.slabs];
        updated[index][field] = value;
        setFormData(prev => ({ ...prev, slabs: updated }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.state_name.strip?.() && !formData.state_name) {
            toast.error("State name is required");
            return;
        }

        // Validate and format slabs
        const formattedSlabs = formData.slabs.map(slab => {
            const min_sal = parseFloat(slab.min_salary) || 0;
            const max_sal = slab.max_salary === '' ? 9999999 : parseFloat(slab.max_salary);
            const amt = parseFloat(slab.amount) || 0;
            const spec_amt = slab.special_amount === '' ? null : parseFloat(slab.special_amount);
            const spec_month = slab.special_month === '' ? null : parseInt(slab.special_month);

            return {
                min_salary: min_sal,
                max_salary: max_sal,
                amount: amt,
                special_amount: spec_amt,
                special_month: spec_month
            };
        });

        setSaving(true);
        try {
            const payload = {
                state_name: formData.state_name,
                is_enabled: formData.is_enabled,
                deduction_frequency: formData.deduction_frequency || 'monthly',
                deduction_months: formData.deduction_months || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                slabs: formattedSlabs
            };

            if (editingConfig) {
                await ptAPI.updateConfig(editingConfig.id, payload);
                toast.success("Professional Tax configuration updated successfully");
            } else {
                await ptAPI.createConfig(payload);
                toast.success("Professional Tax configuration created successfully");
            }
            setIsOpen(false);
            fetchConfigs();
        } catch (error) {
            const detail = error.response?.data?.detail || "Failed to save configuration";
            toast.error(detail);
        } finally {
            setSaving(false);
        }
    };


    const handleDelete = async (id, name) => {
        const confirmed = await confirm({
            title: `Delete Professional Tax Config`,
            description: `Are you sure you want to delete the configuration for "${name}"? This will disable automated calculations for this state.`,
            confirmText: "Delete",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await ptAPI.deleteConfig(id);
            toast.success("Configuration deleted successfully");
            fetchConfigs();
        } catch (error) {
            toast.error("Failed to delete configuration");
        }
    };

    const selectedConfig = configs.find(c => c.id === selectedConfigId);

    const filteredConfigs = configs.filter(cfg => {
        if (activeFilter === 'all') return true;
        if (activeFilter === 'monthly') return cfg.deduction_frequency === 'monthly' && cfg.is_enabled;
        if (activeFilter === 'half_yearly') return cfg.deduction_frequency === 'half_yearly' && cfg.is_enabled;
        if (activeFilter === 'yearly') return cfg.deduction_frequency === 'yearly' && cfg.is_enabled;
        if (activeFilter === 'disabled') return !cfg.is_enabled;
        return true;
    });

    if (loading) {


        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Professional Tax (PT) Configs</h1>
                    <p className="text-muted-foreground">Manage dynamic state-wise salary slabs and Professional Tax deduction amounts</p>
                </div>
                <Button onClick={handleOpenCreate} size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Add State Config
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Left side: States list */}
                <div className="md:col-span-1 space-y-3">
                    <Card className="overflow-hidden border border-slate-200 shadow-sm bg-white">
                        <div className="bg-slate-50 border-b border-slate-100 p-4">
                            <h3 className="font-semibold text-slate-900 text-sm mb-3">Indian States</h3>
                            <div className="flex flex-wrap gap-1.5">
                                {[
                                    { label: 'All', val: 'all' },
                                    { label: 'Monthly', val: 'monthly' },
                                    { label: 'H-Yearly', val: 'half_yearly' },
                                    { label: 'Yearly', val: 'yearly' },
                                    { label: 'Non-PT', val: 'disabled' }
                                ].map(btn => (
                                    <button
                                        key={btn.val}
                                        type="button"
                                        onClick={() => {
                                            setActiveFilter(btn.val);
                                            const matches = configs.filter(cfg => {
                                                if (btn.val === 'all') return true;
                                                if (btn.val === 'monthly') return cfg.deduction_frequency === 'monthly' && cfg.is_enabled;
                                                if (btn.val === 'half_yearly') return cfg.deduction_frequency === 'half_yearly' && cfg.is_enabled;
                                                if (btn.val === 'yearly') return cfg.deduction_frequency === 'yearly' && cfg.is_enabled;
                                                if (btn.val === 'disabled') return !cfg.is_enabled;
                                                return true;
                                            });
                                            if (matches.length > 0) {
                                                setSelectedConfigId(matches[0].id);
                                            }
                                        }}
                                        className={`text-[10px] px-2 py-1 rounded-full font-semibold transition-all ${
                                            activeFilter === btn.val
                                                ? 'bg-slate-950 text-white'
                                                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                                        }`}
                                    >
                                        {btn.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                            {filteredConfigs.length === 0 ? (
                                <div className="p-6 text-center text-xs text-slate-500">No states match this filter.</div>
                            ) : (
                                filteredConfigs.map(cfg => {
                                    const isSelected = cfg.id === selectedConfigId;

                                return (
                                    <button
                                        key={cfg.id}
                                        type="button"
                                        onClick={() => setSelectedConfigId(cfg.id)}
                                        className={`w-full flex items-center justify-between p-4 text-left transition-all ${
                                            isSelected 
                                                ? 'bg-slate-900 text-white font-medium' 
                                                : 'hover:bg-slate-50 text-slate-700 hover:text-slate-900 bg-white'
                                        }`}
                                    >
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold">{cfg.state_name}</span>
                                            <span className={`text-[10px] ${isSelected ? 'text-slate-300' : 'text-slate-500'} capitalize`}>
                                                {(cfg.deduction_frequency || 'monthly').replace('_', ' ')}
                                            </span>
                                        </div>
                                        <span className={`h-2.5 w-2.5 rounded-full border ${
                                            cfg.is_enabled 
                                                ? 'bg-green-500 border-green-600' 
                                                : isSelected 
                                                    ? 'bg-slate-600 border-slate-700' 
                                                    : 'bg-slate-300 border-slate-400'
                                        }`} />
                                    </button>
                                );
                            }))}
                        </div>

                    </Card>
                </div>

                {/* Right side: Detailed slabs */}
                <div className="md:col-span-3">
                    {configs.length === 0 ? (
                        <Card className="border-dashed flex flex-col items-center justify-center p-12 text-center">
                            <Globe className="h-12 w-12 text-muted-foreground mb-4" />
                            <CardTitle className="text-lg font-medium">No State Configurations</CardTitle>
                            <CardDescription className="max-w-xs mt-1">
                                Dynamic PT rules are currently empty. Slabs will fallback to standard default calculations.
                            </CardDescription>
                            <Button onClick={handleOpenCreate} variant="outline" className="mt-6" size="sm">
                                Configure First State
                            </Button>
                        </Card>
                    ) : selectedConfig ? (
                        <Card className={!selectedConfig.is_enabled ? 'opacity-90' : ''}>
                            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
                                <div className="space-y-0.5">
                                    <div className="flex items-center gap-2">
                                        <CardTitle className="text-lg">{selectedConfig.state_name}</CardTitle>
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                            selectedConfig.is_enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'
                                        }`}>
                                            {selectedConfig.is_enabled ? 'Active' : 'Disabled'}
                                        </span>
                                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-semibold capitalize">
                                            {(selectedConfig.deduction_frequency || 'monthly').replace('_', ' ')}
                                        </span>
                                    </div>
                                    <CardDescription>
                                        Deduction Schedule: {selectedConfig.deduction_months && selectedConfig.deduction_months.length < 12 ? `Months [${selectedConfig.deduction_months.join(', ')}]` : 'Every Month'}
                                    </CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(selectedConfig)}>
                                        <Edit className="h-4 w-4 text-slate-500" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(selectedConfig.id, selectedConfig.state_name)}>
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-4">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Min Salary ({selectedConfig.deduction_frequency === 'half_yearly' ? 'Half-Yearly' : selectedConfig.deduction_frequency === 'yearly' ? 'Yearly' : 'Monthly'})</TableHead>
                                            <TableHead>Max Salary ({selectedConfig.deduction_frequency === 'half_yearly' ? 'Half-Yearly' : selectedConfig.deduction_frequency === 'yearly' ? 'Yearly' : 'Monthly'})</TableHead>
                                            <TableHead>Deduction Amount</TableHead>
                                            <TableHead>Special Rule</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {selectedConfig.slabs.map((slab, i) => (
                                            <TableRow key={i}>
                                                <TableCell>₹{slab.min_salary.toLocaleString('en-IN')}</TableCell>
                                                <TableCell>
                                                    {slab.max_salary >= 9999999 ? 'Infinity' : `₹${slab.max_salary.toLocaleString('en-IN')}`}
                                                </TableCell>
                                                <TableCell className="font-semibold text-slate-800">₹{slab.amount}</TableCell>
                                                <TableCell>
                                                    {slab.special_month ? (
                                                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                                            ₹{slab.special_amount} in Month {slab.special_month}
                                                        </span>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed">
                            <Globe className="h-12 w-12 text-muted-foreground mb-4" />
                            <CardTitle className="text-lg font-medium">Select a State</CardTitle>
                            <CardDescription className="max-w-xs mt-1">
                                Choose a state from the list on the left to view and manage its salary slabs.
                            </CardDescription>
                        </Card>
                    )}
                </div>
            </div>

            <Dialog open={isOpen} onOpenChange={setIsOpen}>

                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingConfig ? "Edit PT Config" : "Create PT Config"}</DialogTitle>
                        <DialogDescription>
                            Define slabs and deduction rates for payroll processing. Slabs are evaluated sequentially.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>State Name</Label>
                                <Input
                                    value={formData.state_name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, state_name: e.target.value }))}
                                    placeholder="e.g. Karnataka"
                                    required
                                />
                            </div>
                            <div className="flex items-center space-x-2 pt-8">
                                <Switch
                                    id="enabled-toggle"
                                    checked={formData.is_enabled}
                                    onCheckedChange={(val) => setFormData(prev => ({ ...prev, is_enabled: val }))}
                                />
                                <Label htmlFor="enabled-toggle" className="cursor-pointer">Enable State Calculation</Label>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 border-t pt-4">
                            <div className="space-y-2">
                                <Label>Deduction Frequency</Label>
                                <Select value={formData.deduction_frequency || 'monthly'} onValueChange={handleFrequencyChange}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select frequency" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="monthly">Monthly (Every month)</SelectItem>
                                        <SelectItem value="half_yearly">Half-Yearly (Every 6 months)</SelectItem>
                                        <SelectItem value="yearly">Yearly (Once a year)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Deduction Months</Label>
                                <div className="grid grid-cols-4 gap-2 pt-1">
                                    {[
                                        { label: 'Jan', val: 1 },
                                        { label: 'Feb', val: 2 },
                                        { label: 'Mar', val: 3 },
                                        { label: 'Apr', val: 4 },
                                        { label: 'May', val: 5 },
                                        { label: 'Jun', val: 6 },
                                        { label: 'Jul', val: 7 },
                                        { label: 'Aug', val: 8 },
                                        { label: 'Sep', val: 9 },
                                        { label: 'Oct', val: 10 },
                                        { label: 'Nov', val: 11 },
                                        { label: 'Dec', val: 12 }
                                    ].map(item => {
                                        const checked = (formData.deduction_months || []).includes(item.val);
                                        return (
                                            <label key={item.val} className="flex items-center space-x-1.5 text-xs cursor-pointer bg-slate-50 hover:bg-slate-100 p-1.5 rounded border">
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => handleMonthToggle(item.val)}
                                                />
                                                <span>{item.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>


                        <div className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-2">
                                <Label className="text-base font-semibold">Salary Slabs</Label>
                                <Button type="button" onClick={handleAddSlab} size="xs" variant="outline">
                                    <Plus className="h-3 w-3 mr-1" /> Add Slab
                                </Button>
                            </div>

                            <div className="space-y-3">
                                {formData.slabs.map((slab, index) => (
                                    <div key={index} className="flex gap-2 items-center p-3 border rounded-lg bg-slate-50/50">
                                        <div className="w-[18%]">
                                            <Label className="text-[10px] text-slate-500 uppercase">
                                                {formData.deduction_frequency === 'half_yearly' ? 'Min H-Y Income' : formData.deduction_frequency === 'yearly' ? 'Min Yr Income' : 'Min Salary'}
                                            </Label>
                                            <Input
                                                type="number"
                                                value={slab.min_salary}
                                                onChange={(e) => handleSlabChange(index, 'min_salary', e.target.value)}
                                                placeholder="0"
                                                required
                                            />
                                        </div>
                                        <div className="w-[18%]">
                                            <Label className="text-[10px] text-slate-500 uppercase">
                                                {formData.deduction_frequency === 'half_yearly' ? 'Max H-Y Income' : formData.deduction_frequency === 'yearly' ? 'Max Yr Income' : 'Max Salary'}
                                            </Label>
                                            <Input
                                                type="number"
                                                value={slab.max_salary}
                                                onChange={(e) => handleSlabChange(index, 'max_salary', e.target.value)}
                                                placeholder="Infinity"
                                            />
                                        </div>

                                        <div className="w-[16%]">
                                            <Label className="text-[10px] text-slate-500 uppercase">PT Amount</Label>
                                            <Input
                                                type="number"
                                                value={slab.amount}
                                                onChange={(e) => handleSlabChange(index, 'amount', e.target.value)}
                                                placeholder="200"
                                                required
                                            />
                                        </div>
                                        <div className="w-[20%]">
                                            <Label className="text-[10px] text-slate-500 uppercase">Special Amt (Opt)</Label>
                                            <Input
                                                type="number"
                                                value={slab.special_amount}
                                                onChange={(e) => handleSlabChange(index, 'special_amount', e.target.value)}
                                                placeholder="300"
                                            />
                                        </div>
                                        <div className="w-[16%]">
                                            <Label className="text-[10px] text-slate-500 uppercase">Month (1-12)</Label>
                                            <Input
                                                type="number"
                                                value={slab.special_month}
                                                onChange={(e) => handleSlabChange(index, 'special_month', e.target.value)}
                                                placeholder="2 (Feb)"
                                                min="1"
                                                max="12"
                                            />
                                        </div>
                                        <div className="w-[10%] flex justify-center pt-5">
                                            <Button
                                                type="button"
                                                onClick={() => handleRemoveSlab(index)}
                                                variant="ghost"
                                                size="icon"
                                                disabled={formData.slabs.length <= 1}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <DialogFooter className="border-t pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                Save Config
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            <ConfirmationDialog {...dialogProps} />
        </div>
    );
};

export default ProfessionalTaxSettings;
